import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Key, Loader, Plus } from 'lucide-react';

import { useGetApiKeys } from '@/hooks/api/use-get-api-keys';
import {
  createApiKey,
  updateApiKeyStatus,
  deleteApiKey,
  CreateApiKeyResponse,
} from '@/api/api-keys';
import { toast_success, toast_error } from '@/libs/toast-wrappers';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Table } from '@/components/ui/table';
import { BasicDialog } from '@/components/ui/basic-dialog';
import { BasicDrawer } from '@/components/ui/basic-drawer';
import { InputField } from '@/components/form/input-field';
import { FetchStates } from '@/components/fetch-states';

import { ApiKeyDisplayModal } from './api-key-display-modal';

type ApiKeyManagementProps = {
  userEmail: string;
  readOnly?: boolean;
};

export function ApiKeyManagement({ userEmail, readOnly = false }: ApiKeyManagementProps) {
  const { t } = useTranslation();
  const { apiKeys, loading, error, refetch } = useGetApiKeys(userEmail);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');

  const [newlyCreatedKey, setNewlyCreatedKey] = useState<CreateApiKeyResponse | null>(null);
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      toast_error(t('api-keys-name-required'));
      return;
    }

    setIsCreating(true);
    try {
      const result = await createApiKey(userEmail, newKeyName.trim());
      setNewlyCreatedKey(result);
      setIsDrawerOpen(false);
      setNewKeyName('');
      setIsKeyModalOpen(true);
      await refetch();
      toast_success(t('api-keys-created-success'));
    } catch {
      // Error already handled in API function
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleStatus = async (keyHash: string, currentStatus: 'active' | 'disabled') => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
    setUpdatingStatus(keyHash);
    try {
      await updateApiKeyStatus(keyHash, newStatus);
      await refetch();
      toast_success(t('api-keys-status-updated'));
    } catch {
      // Error already handled in API function
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDeleteClick = (keyHash: string) => {
    setKeyToDelete(keyHash);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!keyToDelete) return;

    setIsDeleting(true);
    try {
      await deleteApiKey(keyToDelete);
      await refetch();
      toast_success(t('api-keys-deleted-success'));
    } catch {
      // Error already handled in API function
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setKeyToDelete(null);
    }
  };

  const handleCloseKeyModal = () => {
    setIsKeyModalOpen(false);
    setNewlyCreatedKey(null);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const tableHeaders = [
    { key: 'name', label: t('api-keys-table-name') },
    {
      key: 'status',
      label: t('api-keys-table-status'),
      format: (value: unknown, row?: Record<string, unknown>) => {
        const status = value as 'active' | 'disabled';
        const keyHash = row?.api_key_hash as string;
        const isUpdating = updatingStatus === keyHash;

        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={status === 'active'}
              onChange={() => void handleToggleStatus(keyHash, status)}
              disabled={isUpdating || readOnly}
            />
            {isUpdating ? (
              <span className="mr-2 animate-spin">
                <Loader className="animate-spin" />
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'created_at',
      label: t('api-keys-table-created'),
      format: (value: unknown) => formatDateShort(value as string),
    },
    {
      key: 'last_used_at',
      label: t('api-keys-table-last-used'),
      format: (value: unknown) => formatDate(value as string | null),
    },
    {
      key: 'actions',
      type: 'actions' as const,
      delete: (row: Record<string, unknown>) => handleDeleteClick(row.api_key_hash as string),
    },
  ].filter((header) => !readOnly || header.key !== 'actions');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Key className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">{t('api-keys-title')}</h3>
        </div>
        {!readOnly && (
          <Button onClick={() => setIsDrawerOpen(true)} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            {t('api-keys-create-button')}
          </Button>
        )}
      </div>

      <p className="text-sm text-muted-foreground">{t('api-keys-description')}</p>

      <FetchStates
        loading={loading}
        error={error?.message}
        refetch={() => void refetch()}
        empty={apiKeys.length === 0}
      >
        {apiKeys.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">{t('api-keys-empty')}</div>
        ) : (
          <div className="border rounded-md overflow-auto">
            <Table
              headers={tableHeaders}
              data={apiKeys}
              disabled={isDeleting || !!updatingStatus}
            />
          </div>
        )}
      </FetchStates>

      {/* Create Key Drawer */}
      <BasicDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={t('api-keys-create-title')}
        size="sm"
      >
        <div className="space-y-6 py-4">
          <p className="text-sm text-muted-foreground">{t('api-keys-create-description')}</p>

          <InputField
            label={t('api-keys-name-label')}
            name="keyName"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder={t('api-keys-name-placeholder')}
            required
            disabled={isCreating}
          />

          <div className="flex gap-3">
            <Button
              onClick={() => void handleCreateKey()}
              loading={isCreating}
              disabled={!newKeyName.trim()}
            >
              {t('api-keys-generate-button')}
            </Button>
            <Button variant="outline" onClick={() => setIsDrawerOpen(false)} disabled={isCreating}>
              {t('default-cancel')}
            </Button>
          </div>
        </div>
      </BasicDrawer>

      {/* New Key Display Modal */}
      <ApiKeyDisplayModal
        apiKey={newlyCreatedKey}
        isOpen={isKeyModalOpen}
        onClose={handleCloseKeyModal}
      />

      {/* Delete Confirmation Dialog */}
      <BasicDialog
        open={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        title={t('api-keys-delete-title')}
        body={t('api-keys-delete-description')}
        type="warning"
        actionText={t('api-keys-delete-confirm')}
        cancelText={t('default-cancel')}
        onActionCallback={() => void handleConfirmDelete()}
      />
    </div>
  );
}
